import { useEffect, useState } from "react";
import CircularProgress from "@mui/material/CircularProgress";
import { listKeys, createKey, deleteKey } from "../api/agentApi";
import { useUser } from "./UserContext";
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Alert,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Autocomplete,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";

interface ApiKey {
  id: string;
  name: string;
  key_type: string;
  description?: string;
  provider: string;
  created_at: string;
  last_used?: string;
  status: "active" | "inactive" | "expired";
}

export default function KeyManager() {
  const [keys, setKeys] = useState<ApiKey[]>([]);
  const [deleting, setDeleting] = useState("");
  const [error, setError] = useState("");
  const [openDialog, setOpenDialog] = useState(false);
  const [newKey, setNewKey] = useState({
    name: "",
    description: "",
    value: "",
    provider: "",
  });
  const [isAddingKey, setIsAddingKey] = useState(false);
  const { user } = useUser();

  // Fetch keys from API
  const fetchKeys = async () => {
    try {
      const res = await listKeys();
      setKeys(res.keys || []);
    } catch (e) {
      setError("Failed to fetch keys.");
    }
  };

  useEffect(() => {
    fetchKeys();
    // eslint-disable-next-line
  }, []);

  const handleDelete = async (keyId: string) => {
    if (!window.confirm(`Delete key '${keyId}'? This cannot be undone.`)) return;
    setDeleting(keyId);
    try {
      await deleteKey(keyId);
      await fetchKeys();
    } catch (e) {
      setError("Failed to delete key.");
    }
    setDeleting("");
  };

  const handleOpenDialog = () => {
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setNewKey({ name: "", description: "", value: "", provider: "" });
  };

  const handleAddKey = async () => {
    setError("");
    if (!newKey.name || !newKey.value) {
      setError("Key Name and Key Value are required.");
      return;
    }
    setIsAddingKey(true);
    try {
      await createKey({
        name: newKey.name,
        value: newKey.value,
        description: newKey.description,
        provider: newKey.provider,
      });
      await fetchKeys();
      handleCloseDialog();
    } catch (e: any) {
      setError(e?.message || "Failed to add key.");
    } finally {
      setIsAddingKey(false);
    }
  };

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
          mt: 1,
        }}
      >
        <Typography variant="h4">Key Manager</Typography>
      </Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {user?.role === "admin" && (
        <Button
          onClick={handleOpenDialog}
          variant="contained"
          color="primary"
          sx={{
            mt: 3,
            fontSize: "1rem",
            px: 3,
            py: 1,
            borderRadius: "99px",
            textTransform: "none",
          }}
        >
          Add Keys
        </Button>
      )}
      <TableContainer component={Paper} sx={{ mt: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: "#f5f5f5" }}>
              <TableCell sx={{ fontWeight: 700, fontSize: "1rem" }}>
                Key Name
              </TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: "1rem" }}>
                Key Type
              </TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: "1rem", width: 400 }}>
                Description
              </TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: "1rem" }}>
                Provider
              </TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: "1rem" }}>
                Created
              </TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: "1rem" }}>
                Last Used
              </TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: "1rem" }}>
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {keys.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} sx={{ textAlign: "center", py: 8 }}>
                  <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
                    No API Keys Found
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {user?.role === "admin"
                      ? "Click 'Add Keys' to create your first API key."
                      : "No keys have been configured yet."}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              keys.map((key) => (
              <TableRow key={key.id} hover sx={{ fontSize: "0.95rem" }}>
                <TableCell sx={{ fontSize: "0.95rem", py: 1 }}>
                  {key.name}
                </TableCell>
                <TableCell sx={{ fontSize: "0.95rem", py: 1 }}>
                  <Chip
                    label={key.key_type.replace('_', ' ')}
                    size="small"
                    sx={{
                      bgcolor: "#e3f2fd",
                      color: "#1976d2",
                      fontWeight: 500,
                      textTransform: "capitalize",
                    }}
                  />
                </TableCell>
                <TableCell sx={{ fontSize: "0.95rem", py: 1 }}>
                  {key.description || "—"}
                </TableCell>
                <TableCell sx={{ fontSize: "0.95rem", py: 1 }}>
                  {key.provider}
                </TableCell>
                <TableCell sx={{ fontSize: "0.95rem", py: 1 }}>
                  {key.created_at}
                </TableCell>
                <TableCell sx={{ fontSize: "0.95rem", py: 1 }}>
                  {key.last_used || "Never"}
                </TableCell>
                <TableCell sx={{ py: 1 }}>
                  <Button
                    startIcon={<DeleteIcon />}
                    size="small"
                    color="error"
                    onClick={() => handleDelete(key.id)}
                    disabled={deleting === key.id}
                  >
                    Delete
                  </Button>
                </TableCell>
              </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Add Key Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Add New Key</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 2 }}>
            {error && (
              <Alert severity="error">{error}</Alert>
            )}
            <TextField
              label="Key Name"
              value={newKey.name}
              onChange={(e) => setNewKey({ ...newKey, name: e.target.value })}
              fullWidth
              required
              autoComplete="off"
            />
            <Autocomplete
              freeSolo
              options={[
                "OpenAI",
                "Anthropic",
                "Google",
                "AWS",
                "Azure",
                "HuggingFace",
                "Cohere",
                "Mistral",
                "Gemini",
                "GitHub",
                "GitLab",
                "Slack",
                "Microsoft",
                "Salesforce",
                "Stripe",
                "Twilio",
                "SendGrid",
                "PostgreSQL",
                "MongoDB",
                "Redis",
                "Brex",
              ]}
              value={newKey.provider}
              onChange={(_, newValue) => setNewKey({ ...newKey, provider: newValue || "" })}
              onInputChange={(_, newInputValue) => setNewKey({ ...newKey, provider: newInputValue })}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Provider (optional)"
                  placeholder="Select or type a custom provider"
                />
              )}
            />
            <TextField
              label="Description (optional)"
              value={newKey.description}
              onChange={(e) => setNewKey({ ...newKey, description: e.target.value })}
              fullWidth
              multiline
              rows={2}
              autoComplete="off"
            />
            <TextField
              label="Key Value"
              value={newKey.value}
              onChange={(e) => setNewKey({ ...newKey, value: e.target.value })}
              fullWidth
              required
              autoComplete="off"
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCloseDialog} color="inherit" disabled={isAddingKey}>
            Cancel
          </Button>
          <Button onClick={handleAddKey} variant="contained" color="primary" disabled={isAddingKey} startIcon={isAddingKey ? <CircularProgress size={20} /> : null}>
            {isAddingKey ? "Adding..." : "Add Key"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
